// src/layouts/CustomLayout.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Layout, LayoutProps, useLogout, useNotify, useTranslate } from 'react-admin';
import { useTheme, useMediaQuery, alpha } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { CustomMenu } from './CustomMenu';
import { CustomAppBar } from './CustomAppBar';
import { TokenExpiryDialog } from '../components/dialogs/TokenExpiryDialog';
import { decodeJWT } from '../utils/auth';
import { API_CONFIG } from '../config/constants'; 

export const CustomLayout: React.FC<LayoutProps> = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const isDashboard = location.pathname === '/';
    const logout = useLogout();
    const notify = useNotify();
    const translate = useTranslate();
    
    // Token expiry state
    const [showExpiryDialog, setShowExpiryDialog] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(300); // 5 minutos por defecto
    const [tokenExpiryTime, setTokenExpiryTime] = useState<number | null>(null);
    
    // Configuración
    const WARNING_THRESHOLD = 300; // 5 minutos antes de expirar
    const CHECK_INTERVAL = 10000; // Verificar cada 10 segundos
    const COUNTDOWN_INTERVAL = 1000; // Actualizar countdown cada segundo



// Función para renovar el token
const renewToken = useCallback(async () => {
    try {
        const currentToken = localStorage.getItem('token');
        if (!currentToken) {
         
            logout();
            return;
        }

     
        
        const url = `${API_CONFIG.BASE_URL}/auth/refresh`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${currentToken}`
            }
        });

  

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
    
        
        const data = responseData.data || responseData;
        
        if (data?.token) {
            // Guardar nuevo token
            localStorage.setItem('token', data.token);
            
            // Actualizar información del usuario si viene en la respuesta
            if (data.user) {
                localStorage.setItem('user', JSON.stringify(data.user));
                localStorage.setItem('role', data.user.role);
                
                if (data.user.hotelId) {
                    localStorage.setItem('hotelId', data.user.hotelId);
                }
                if (data.user.groupId) {
                    localStorage.setItem('groupId', data.user.groupId);
                }
            }
            
            // Actualizar el tiempo de expiración si viene en la respuesta
            if (data.expiresIn) {
                const expirationTime = new Date().getTime() + (data.expiresIn * 1000);
                localStorage.setItem('tokenExpiration', expirationTime.toString());
            }
            
            // IMPORTANTE: Cerrar el diálogo inmediatamente después de renovar con éxito
            setShowExpiryDialog(false);
            setSecondsRemaining(300); // Resetear el contador
            
            // Intentar decodificar el nuevo token para actualizar el tiempo de expiración
            try {
                const newDecodedToken = decodeJWT(data.token);
                if (newDecodedToken?.exp) {
                    setTokenExpiryTime(newDecodedToken.exp);
                    
                    const expirationDate = new Date(newDecodedToken.exp * 1000);
                    notify(translate('common.notifications.session_extended_with_time', { time: expirationDate.toLocaleTimeString() }), { 
                        type: 'success',
                        autoHideDuration: 5000 
                    });
                    
                } else {
                    // Si no hay exp pero el token se renovó, mostrar mensaje genérico
                    notify(translate('common.notifications.session_extended_success'), { 
                        type: 'success',
                        autoHideDuration: 5000 
                    });
                
                }
            } catch (decodeError) {
                // Si falla el decode, pero el token se renovó, continuar
                console.error('Error decodificando el nuevo token:', decodeError);
                notify(translate('common.notifications.session_extended_success'), { 
                    type: 'success',
                    autoHideDuration: 5000 
                });
            }
            
        } else {
            throw new Error('No se recibió un nuevo token del servidor');
        }
    } catch (error: any) {
        console.error('Error renovando token:', error);
        
        // Manejo específico de errores
        if (error.message?.includes('404')) {
            // El endpoint no existe
            notify(
                'La función de extender sesión aún no está disponible. Por favor, guarda tu trabajo.', 
                { type: 'warning', autoHideDuration: 10000 }
            );
            setShowExpiryDialog(false);
            
            // Dar más tiempo antes de mostrar el diálogo nuevamente
            setTimeout(() => {
                setShowExpiryDialog(true);
            }, 120000); // 2 minutos
            
        } else if (error.message?.includes('401') || error.message?.includes('403')) {
            // Token inválido o usuario no autorizado
            notify(translate('common.notifications.session_expired'), { 
                type: 'error' 
            });
            setTimeout(() => logout(), 2000);
            
        } else if (error.message?.includes('Failed to fetch')) {
            // Error de red
            notify(translate('common.notifications.connection_error'), { 
                type: 'error' 
            });
            setShowExpiryDialog(false);
            
        } else {
            // Otros errores
            notify(translate('common.notifications.session_extend_error'), { 
                type: 'error' 
            });
            setShowExpiryDialog(false);
            
            // Dar tiempo al usuario para guardar su trabajo
            setTimeout(() => {
                notify(translate('common.notifications.session_will_expire'), { 
                    type: 'warning',
                    autoHideDuration: 10000 
                });
            }, 30000); // 30 segundos
        }
    }
}, [logout, notify, translate, setShowExpiryDialog, setSecondsRemaining, setTokenExpiryTime]);

    // Función para verificar la expiración del token
    const checkTokenExpiry = useCallback(() => {
        const token = localStorage.getItem('token');
        
        if (!token) {
            setShowExpiryDialog(false);
            return;
        }
        
        const decodedToken = decodeJWT(token);
        
        if (!decodedToken || !decodedToken.exp) {
            setShowExpiryDialog(false);
            return;
        }
        
        const currentTime = Math.floor(Date.now() / 1000);
        const timeLeft = decodedToken.exp - currentTime;

        // Si el token ya expiró, hacer logout
        if (timeLeft <= 0) {
        
            logout();
            return;
        }

        // Si está dentro del umbral de advertencia, mostrar diálogo
        if (timeLeft > 0 && timeLeft <= WARNING_THRESHOLD) {
            setSecondsRemaining(timeLeft);
            setTokenExpiryTime(decodedToken.exp);
            
            if (!showExpiryDialog) {
           
                setShowExpiryDialog(true);
            }
        } else {
            // Si todavía hay tiempo suficiente, ocultar diálogo
            if (showExpiryDialog) {
             
                setShowExpiryDialog(false);
            }
        }
    }, [showExpiryDialog, logout]);

    // Effect para verificar expiración del token periódicamente
    useEffect(() => {
        // Verificación inicial
        checkTokenExpiry();
        
        // Configurar intervalo de verificación
        const checkInterval = setInterval(checkTokenExpiry, CHECK_INTERVAL);
        
        return () => clearInterval(checkInterval);
    }, [checkTokenExpiry]);

    // Effect para actualizar el countdown cuando el diálogo está abierto
    useEffect(() => {
        if (!showExpiryDialog || !tokenExpiryTime) return;

        const countdownInterval = setInterval(() => {
            const currentTime = Math.floor(Date.now() / 1000);
            const timeLeft = tokenExpiryTime - currentTime;
            
            if (timeLeft <= 0) {
                logout();
            } else {
                setSecondsRemaining(timeLeft);
            }
        }, COUNTDOWN_INTERVAL);

        return () => clearInterval(countdownInterval);
    }, [showExpiryDialog, tokenExpiryTime, logout]);

    // Handler para extender sesión
    const handleExtendSession = () => {
        renewToken();
    };

    return (
        <>
            <Layout
                {...props}
                appBar={CustomAppBar}
                menu={CustomMenu}
                sx={{
                    // 1. Fondo general de la página
                    '&': {
                        backgroundColor: theme.palette.background.default,
                    },
                    
                    // --- LÓGICA SOLO PARA ESCRITORIO ---
                    // Fija la altura del layout a la de la pantalla y prohíbe el scroll global.
                    ...(!isMobile && {
                        height: '100vh',
                        overflow: 'hidden',
                    }),

                    // 2. El marco principal que contiene todo
                    '& .RaLayout-appFrame': {
                        marginTop: 0,
                        alignItems: 'flex-start',
                    },

                    // 3. Contenedor que agrupa Menú y Contenido
                    '& .RaLayout-contentWithSidebar': {
                        display: 'flex',
                        flex: 1, 
                        gap: isMobile ? 0 : (theme as any).custom?.layout?.gap || '20px',
                        width: '100%',
                        ...(!isMobile && {
                            padding: (theme as any).custom?.layout?.desktopContainerPadding || '24px',
                            overflow: 'hidden', 
                        }),
                    },

                    '& .RaLayout-content': {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0, // Permite que se encoja correctamente

                        // --- LÓGICA SOLO PARA MÓVIL ---
                        // Permite que la página crezca y se desplace de forma natural.
                        ...(isMobile && {
                            minHeight: 'calc(100vh - 88px)',
                            paddingTop: '64px', // Altura del AppBar
                            paddingBottom: (theme as any).custom?.layout?.mobileContentPaddingBottom || '20px',
                        }),

                        // --- LÓGICA SOLO PARA ESCRITORIO ---
                        // Crea un contenedor scrolleable para el contenido en escritorio.
                        ...(!isMobile && {
                           
                            overflow: 'hidden',
                        }),
                    },

                    // 4. La "Tarjeta" blanca de contenido
                    '& .RaLayout-content > div': {
                        width: '100%',
                     
                        backgroundColor: isDashboard 
                            ? alpha(theme.palette.background.paper, 0.6) // Fondo translúcido para dashboard
                            : theme.palette.background.paper,

                        borderRadius: (theme as any).custom?.layout?.card?.borderRadius,
                        boxShadow: (theme as any).custom?.layout?.card?.boxShadow,
                       
                        flex: 1,
                        maxHeight: 'calc(100vh - 74px)',
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        
                         
                    },
                }}
            >
                {props.children}
            </Layout>

            {/* Diálogo de expiración de token */}
            {showExpiryDialog && (
                <TokenExpiryDialog
                open={showExpiryDialog}
                secondsRemaining={secondsRemaining}
                onExtendSession={handleExtendSession}
                onLogout={logout}
            />
            )}
        </>
    );
};

export default CustomLayout;